/**
 * LockerScreen — the lobby cosmetics picker (spec 6A).
 *
 * Drives the static #lockerScreen shell in index.html the way SplashScreen
 * drives #splashScreen: caches ids, fills the tab strip, the card grid and the
 * preview panel from a Loadout (what is equipped), a store (what is owned) and
 * config/cosmetics.json (what exists). Clicking an owned card equips it;
 * locked cards are inert; X and Escape close. No side-effect imports and no
 * pixi here — the live preview arrives through the injected createPreview().
 */
import { renderTrailSwatch } from './trailSwatch.js';

/** One row per cosmetic slot. A new kind of cosmetic is a row here plus an art renderer below. */
const TABS = [
  { id: 'skins', label: 'Skins', slot: 'skin', configKey: 'playerSkins', enabled: true },
  { id: 'trails', label: 'Trails', slot: 'trail', configKey: 'trails', enabled: true },
  { id: 'backpacks', label: 'Backpacks', slot: 'backpack', configKey: 'backpacks', enabled: true }
];

const RARITY_ORDER = ['COMMON', 'UNCOMMON', 'RARE', 'EPIC', 'LEGENDARY'];

const LockerScreen = {
  TABS,
  els: {},
  deps: {},
  activeTabId: 'skins',
  preview: null,
  previewPromise: null,
  hovered: null,
  onKeyDown: null,

  init(deps) {
    this.destroy();                 // a re-init means new collaborators: drop a preview built from the old ones
    this.detachKeys();              // …and any Escape listener the previous init left behind
    this.activeTabId = TABS[0].id;
    this.hovered = null;
    this.deps = {
      showNotification: () => {}, onClose: () => {}, createPreview: null,
      getSkinImage: () => null, getBackpackImage: () => null, ...deps
    };
    const byId = (id) => document.getElementById(id);
    this.els = {
      root: byId('lockerScreen'), close: byId('lockerClose'), tabs: byId('lockerTabs'), grid: byId('lockerGrid'),
      status: byId('lockerStatus'), previewHost: byId('lockerPreviewHost'), previewName: byId('lockerPreviewName'),
      previewRarity: byId('lockerPreviewRarity'), previewDesc: byId('lockerPreviewDesc'), previewBadge: byId('lockerPreviewBadge')
    };
    this.els.close?.addEventListener('click', () => this.hide());
    this.deps.loadout.subscribe(() => { if (this.isVisible()) this.render(); });
  },

  isVisible() {
    return !!this.els.root && !this.els.root.classList.contains('hidden');
  },

  async show() {
    if (!this.els.root || this.isVisible()) return;   // re-opening an open locker would leak a second Escape listener
    this.els.root.classList.remove('hidden');
    this.onKeyDown = (e) => { if (e.key === 'Escape') this.hide(); };
    document.addEventListener('keydown', this.onKeyDown);
    this.render();
    await this.ensurePreview();
    this.preview?.start();
    this.updatePreview();
  },

  hide() {
    if (!this.els.root) return;
    this.els.root.classList.add('hidden');
    this.detachKeys();
    this.hovered = null;
    this.preview?.stop();
    this.deps.onClose();
  },

  /** Exactly one Escape listener can ever be attached: show() adds, hide()/init() take it away. */
  detachKeys() {
    if (this.onKeyDown) document.removeEventListener('keydown', this.onKeyDown);
    this.onKeyDown = null;
  },

  selectTab(id) {
    const tab = TABS.find((t) => t.id === id);
    if (!tab || !tab.enabled) return;
    this.activeTabId = id;
    this.render();
    this.updatePreview();
  },

  activeTab() {
    return TABS.find((t) => t.id === this.activeTabId) || TABS[0];
  },

  items(tab) {
    return tab.configKey ? (this.deps.cosmeticsConfig?.[tab.configKey] || []) : [];
  },

  /** 'equipped' | 'owned' | 'locked' (spec D3) */
  itemState(slot, id) {
    if (this.deps.loadout.get()[slot] === id) return 'equipped';
    return this.deps.store.owns(slot, id) ? 'owned' : 'locked';
  },

  render() {
    this.renderTabs();
    this.renderStatus();
    this.renderGrid();
  },

  renderTabs() {
    const { tabs } = this.els;
    if (!tabs) return;
    tabs.innerHTML = '';
    for (const tab of TABS) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'locker-tab' + (tab.id === this.activeTabId ? ' active' : '');
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-selected', String(tab.id === this.activeTabId));
      b.disabled = !tab.enabled;
      b.textContent = tab.label;
      if (tab.note) { const s = document.createElement('span'); s.className = 'soon'; s.textContent = tab.note; b.appendChild(s); }
      b.addEventListener('click', () => this.selectTab(tab.id));
      tabs.appendChild(b);
    }
  },

  renderStatus() {
    const { status } = this.els;
    if (!status) return;
    if (this.deps.store.status !== 'failed') { status.classList.add('hidden'); status.innerHTML = ''; return; }
    status.classList.remove('hidden');
    status.innerHTML = '';
    const msg = document.createElement('span');
    msg.textContent = "Couldn't load your locker — only default items are available until it reconnects.";
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.textContent = 'Retry';
    retry.addEventListener('click', async () => { await this.deps.store.load(); this.render(); this.updatePreview(); });
    status.append(msg, retry);
  },

  renderGrid() {
    const { grid } = this.els;
    if (!grid) return;
    const tab = this.activeTab();
    grid.innerHTML = '';
    const items = this.items(tab);
    if (items.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'locker-empty';
      empty.textContent = 'Nothing here yet.';
      grid.appendChild(empty);
      return;
    }
    for (const item of items) grid.appendChild(this.buildCard(tab, item));
  },

  buildCard(tab, item) {
    const state = this.itemState(tab.slot, item.id);
    const card = document.createElement('button');
    card.type = 'button';
    card.className = `locker-card ${state}`;
    card.dataset.id = item.id;
    card.setAttribute('aria-pressed', String(state === 'equipped'));
    if (state === 'locked') { card.setAttribute('aria-disabled', 'true'); card.title = 'Not unlocked yet'; }

    const art = document.createElement('div');
    art.className = 'locker-card-art';
    art.appendChild(this.buildArt(tab, item));
    const name = document.createElement('div');
    name.className = 'locker-card-name';
    name.textContent = item.name || item.id;
    const badge = document.createElement('span');
    badge.className = 'locker-card-badge';
    badge.textContent = state === 'equipped' ? '✔' : state === 'locked' ? '🔒' : '';
    card.append(art, name, badge);

    card.addEventListener('click', () => {
      if (state === 'locked') { card.classList.remove('flash'); void card.offsetWidth; card.classList.add('flash'); return; }
      this.deps.loadout.equip(tab.slot, item.id);   // subscribe() re-renders
      this.hovered = null;
      this.updatePreview();
    });
    card.addEventListener('mouseenter', () => { this.hovered = { slot: tab.slot, item }; this.updatePreview(); });
    card.addEventListener('mouseleave', () => { this.hovered = null; this.updatePreview(); });
    return card;
  },

  buildArt(tab, item) {
    if (tab.slot === 'trail') {
      const canvas = document.createElement('canvas');
      canvas.width = 96; canvas.height = 40;
      const ctx = canvas.getContext('2d');
      if (ctx) renderTrailSwatch(ctx, item, { width: 96, height: 40 });
      return canvas;
    }
    if (tab.slot === 'backpack' && !item.sprite) {
      // The "no backpack" choice: a crossed-out circle
      const none = document.createElement('div');
      none.className = 'locker-card-none';
      none.setAttribute('aria-hidden', 'true');
      return none;
    }
    const img = document.createElement('img');
    img.alt = item.name || item.id;
    img.src = tab.slot === 'skin' ? this.deps.getSkinImage(item.id)?.src || item.sprite || '' : item.sprite || '';
    return img;
  },

  /** The preview panel shows the hovered item, else the equipped item of the active tab. */
  updatePreview() {
    const tab = this.activeTab();
    const loadout = this.deps.loadout.get();
    const shown = this.hovered || { slot: tab.slot, item: this.items(tab).find((i) => i.id === loadout[tab.slot]) };
    const item = shown.item;
    if (!item) return;
    const { previewName, previewRarity, previewDesc, previewBadge, previewHost } = this.els;
    if (previewName) previewName.textContent = item.name || item.id;
    if (previewRarity) {
      const rarity = RARITY_ORDER.includes(item.rarity) ? item.rarity : 'COMMON';
      previewRarity.textContent = rarity;
      previewRarity.dataset.rarity = rarity;
    }
    if (previewDesc) previewDesc.textContent = item.description || '';
    const state = this.itemState(shown.slot, item.id);
    if (previewBadge) {
      previewBadge.textContent = state === 'equipped' ? 'EQUIPPED' : state === 'locked' ? 'LOCKED' : 'Click to equip';
      previewBadge.classList.toggle('hint', state !== 'equipped');
    }
    const previewLoadout = { ...loadout, [shown.slot]: item.id };
    const skinEntry = (this.deps.cosmeticsConfig?.playerSkins || []).find((s) => s.id === previewLoadout.skin);
    previewHost?.parentElement?.style.setProperty('--glow', skinEntry?.glowColor || 'transparent');

    if (this.preview) {
      this.preview.setLoadout(previewLoadout, this.deps.cosmeticsConfig);
      this.preview.setSkinImage(this.deps.getSkinImage(previewLoadout.skin) || null);
      this.preview.setBackpackImage(this.deps.getBackpackImage(previewLoadout.backpack) || null);
    } else if (previewHost) {
      // No WebGL preview (tests, or the factory failed): a static sprite at preview size
      let img = previewHost.querySelector('img');
      if (!img) { img = document.createElement('img'); img.alt = ''; previewHost.appendChild(img); }
      img.src = this.deps.getSkinImage(previewLoadout.skin)?.src || skinEntry?.sprite || '';
    }
  },

  async ensurePreview() {
    if (this.preview || !this.deps.createPreview || !this.els.previewHost) return;
    if (!this.previewPromise) {
      this.previewPromise = Promise.resolve()
        .then(() => this.deps.createPreview(this.els.previewHost))
        .catch(() => null);
    }
    this.preview = await this.previewPromise;
    if (!this.preview) this.previewPromise = null;   // allow a retry on the next open
  },

  destroy() {
    this.preview?.destroy();
    this.preview = null;
    this.previewPromise = null;
  }
};

export { LockerScreen, TABS as LOCKER_TABS };
